import React, { useState, useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Button,
  Grid,
  TextField,
  MenuItem,
  Chip,
  Avatar,
  makeStyles,
  Divider
} from '@material-ui/core';
import { 
  ChevronLeft,
  ChevronRight,
  Today,
  AccessTime,
  Event,
  EventBusy,
  EventAvailable,
  Person,
  PlayCircleFilled,
  CheckCircle,
  Schedule,
  Help,
  Clear
} from '@material-ui/icons';
import { getMeetingStatus } from '../utils/meetingUtils';

const useStyles = makeStyles((theme) => ({
  calendarContainer: {
    padding: theme.spacing(3),
  },
  dateNavigation: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(3),
    borderRadius: 12,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
  },
  dateTitle: {
    fontWeight: 600,
    fontSize: '1.5rem',
  },
  todayBadge: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
    fontSize: '0.75rem',
    padding: '2px 8px',
    borderRadius: 12,
    marginTop: theme.spacing(0.5),
  },
  navButton: {
    color: 'white',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
    },
  },
  todayButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
    },
  },
  filtersCard: {
    marginBottom: theme.spacing(3),
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  meetingsCard: {
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  meetingItem: {
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: '1px solid #e0e0e0',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
    },
  },
  meetingInProgress: {
    borderLeft: '4px solid #4caf50',
    backgroundColor: 'rgba(76, 175, 80, 0.05)',
  },
  meetingNotStarted: {
    borderLeft: '4px solid #2196f3',
    backgroundColor: 'rgba(33, 150, 243, 0.05)',
  },
  meetingEnded: {
    borderLeft: '4px solid #9e9e9e',
    backgroundColor: 'rgba(158, 158, 158, 0.05)',
  },
  timeDisplay: {
    textAlign: 'center',
    minWidth: 80,
  },
  timeStart: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  timeEnd: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  meetingTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  organizerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  avatar: {
    width: 48,
    height: 48,
    fontSize: '1rem',
    fontWeight: 600,
  },
  statusChip: {
    fontWeight: 500,
    borderRadius: 16,
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
  clearFiltersButton: {
    marginTop: theme.spacing(2),
    borderColor: '#667eea',
    color: '#667eea',
    '&:hover': {
      borderColor: '#5a67d8',
      backgroundColor: 'rgba(102, 126, 234, 0.04)',
    },
  },
}));

const CalendarView = ({ meetings, onMeetingClick }) => {
  const classes = useStyles();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [timeStartFilter, setTimeStartFilter] = useState('');
  const [timeEndFilter, setTimeEndFilter] = useState('');

  const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const getMeetingsForDate = (date) => {
    const localDateStart = new Date(date);
    localDateStart.setHours(0, 0, 0, 0);
    const localDateEnd = new Date(date);
    localDateEnd.setHours(23, 59, 59, 999);
    
    return meetings.filter((meeting) => {
      let meetingDate;
      const hasTz = meeting.scheduledStart.endsWith('Z') || meeting.scheduledStart.match(/[-+]\d{2}:?\d{2}$/);
      
      if (!hasTz && meeting.timeZone === 'UTC') {
        const cleanDate = meeting.scheduledStart.replace(/\.\d+$/, '') + 'Z';
        meetingDate = new Date(cleanDate);
      } else {
        meetingDate = new Date(meeting.scheduledStart);
      }
      
      return meetingDate >= localDateStart && meetingDate <= localDateEnd;
    });
  };

  const filteredMeetings = useMemo(() => {
    let filtered = getMeetingsForDate(selectedDate);

    if (timeStartFilter) {
      filtered = filtered.filter((m) => {
        let meetingTime;
        const hasTz = m.scheduledStart.endsWith('Z') || m.scheduledStart.match(/[-+]\d{2}:?\d{2}$/);
        
        if (!hasTz && m.timeZone === 'UTC') {
          const cleanDate = m.scheduledStart.replace(/\.\d+$/, '') + 'Z';
          meetingTime = new Date(cleanDate);
        } else {
          meetingTime = new Date(m.scheduledStart);
        }
        
        const [hours, minutes] = timeStartFilter.split(':').map(Number);
        const filterTime = new Date(selectedDate);
        filterTime.setHours(hours, minutes, 0, 0);
        
        return meetingTime >= filterTime;
      });
    }

    if (timeEndFilter) {
      filtered = filtered.filter((m) => {
        let meetingTime;
        const hasTz = m.scheduledStart.endsWith('Z') || m.scheduledStart.match(/[-+]\d{2}:?\d{2}$/);
        
        if (!hasTz && m.timeZone === 'UTC') {
          const cleanDate = m.scheduledStart.replace(/\.\d+$/, '') + 'Z';
          meetingTime = new Date(cleanDate);
        } else {
          meetingTime = new Date(m.scheduledStart);
        }
        
        const [hours, minutes] = timeEndFilter.split(':').map(Number);
        const filterTime = new Date(selectedDate);
        filterTime.setHours(hours, minutes, 0, 0);
        
        return meetingTime <= filterTime;
      });
    }

    return filtered.sort((a, b) => {
      let dateA, dateB;
      
      const hasTzA = a.scheduledStart.endsWith('Z') || a.scheduledStart.match(/[-+]\d{2}:?\d{2}$/);
      if (!hasTzA && a.timeZone === 'UTC') {
        dateA = new Date(a.scheduledStart.replace(/\.\d+$/, '') + 'Z');
      } else {
        dateA = new Date(a.scheduledStart);
      }
      
      const hasTzB = b.scheduledStart.endsWith('Z') || b.scheduledStart.match(/[-+]\d{2}:?\d{2}$/);
      if (!hasTzB && b.timeZone === 'UTC') {
        dateB = new Date(b.scheduledStart.replace(/\.\d+$/, '') + 'Z');
      } else {
        dateB = new Date(b.scheduledStart);
      }
      
      return dateA.getTime() - dateB.getTime();
    });
  }, [meetings, selectedDate, timeStartFilter, timeEndFilter]);

  const navigateDay = (direction) => {
    setSelectedDate((prev) => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setDate(prev.getDate() - 1);
      } else {
        newDate.setDate(prev.getDate() + 1);
      }
      return newDate;
    });
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const getStatusConfig = (status) => {
    const configs = {
      'in_progress': { 
        label: 'En Curso', 
        color: 'primary',
        className: classes.meetingInProgress,
        icon: PlayCircleFilled
      },
      'ended': { 
        label: 'Finalizada', 
        color: 'default',
        className: classes.meetingEnded,
        icon: CheckCircle
      },
      'not_started': { 
        label: 'No Iniciada', 
        color: 'secondary',
        className: classes.meetingNotStarted,
        icon: Schedule
      },
    };
    return configs[status] || { 
      label: 'Desconocido', 
      color: 'default',
      className: '',
      icon: Help
    };
  };

  const formatTime = (dateString, timeZone) => {
    let date;
    
    const hasTz = dateString.endsWith('Z') || dateString.match(/[-+]\d{2}:?\d{2}$/);
    
    if (!hasTz && timeZone === 'UTC') {
      const cleanDate = dateString.replace(/\.\d+$/, '') + 'Z';
      date = new Date(cleanDate);
    } else {
      date = new Date(dateString);
    }
    
    return date.toLocaleTimeString('es-ES', { 
      hour: '2-digit', 
      minute: '2-digit'
    });
  };

  const formatDate = (date) => {
    return `${dayNames[date.getDay()]}, ${date.getDate()} de ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
  };

  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        options.push(timeString);
      }
    }
    return options;
  };

  const timeOptions = useMemo(() => generateTimeOptions(), []);

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (meetings.length === 0) {
    return (
      <Card className={classes.meetingsCard}>
        <CardContent>
          <Box className={classes.emptyState}>
            <EventBusy style={{ fontSize: 48, marginBottom: 16, color: 'inherit' }} />
            <Typography variant="h6" gutterBottom>
              No se encontraron reuniones
            </Typography>
            <Typography variant="body2">
              Ajusta los filtros para ver más resultados
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box className={classes.calendarContainer}>
      {/* Date Navigation */}
      <Card className={classes.dateNavigation}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton
              onClick={() => navigateDay('prev')}
              className={classes.navButton}
              size="small"
            >
              <ChevronLeft />
            </IconButton>
            <Box>
              <Typography className={classes.dateTitle}>
                {formatDate(selectedDate)}
              </Typography>
              {isToday(selectedDate) && (
                <Box className={classes.todayBadge} component="span">
                  Hoy
                </Box>
              )}
            </Box>
            <IconButton
              onClick={() => navigateDay('next')}
              className={classes.navButton}
              size="small"
            >
              <ChevronRight />
            </IconButton>
          </Box>
          <Button
            onClick={goToToday}
            className={classes.todayButton}
            startIcon={<Today />}
          >
            Ir a hoy
          </Button>
        </Box>
      </Card>

      {/* Time Filters */}
      <Card className={classes.filtersCard}>
        <CardContent>
          <Typography variant="h6" gutterBottom style={{ color: '#667eea', fontWeight: 600 }}>
            <AccessTime style={{ marginRight: 8, verticalAlign: 'middle', color: '#667eea' }} />
            Filtros de Hora
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Hora desde"
                variant="outlined"
                size="small"
                value={timeStartFilter}
                onChange={(e) => setTimeStartFilter(e.target.value)}
                displayEmpty
                InputLabelProps={{
                  shrink: true,
                }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) => {
                    if (!selected || selected === '') {
                      return 'Todas las horas';
                    }
                    return selected;
                  },
                }}
              >
                <MenuItem value="">Todas las horas</MenuItem>
                {timeOptions.map((time) => (
                  <MenuItem key={time} value={time}>
                    {time}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Hora hasta"
                variant="outlined"
                size="small"
                value={timeEndFilter}
                onChange={(e) => setTimeEndFilter(e.target.value)}
                displayEmpty
                InputLabelProps={{
                  shrink: true,
                }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) => {
                    if (!selected || selected === '') {
                      return 'Todas las horas';
                    }
                    return selected;
                  },
                }}
              >
                <MenuItem value="">Todas las horas</MenuItem>
                {timeOptions.map((time) => (
                  <MenuItem key={time} value={time}>
                    {time}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
          {(timeStartFilter || timeEndFilter) && (
            <Button
              onClick={() => {
                setTimeStartFilter('');
                setTimeEndFilter('');
              }}
              className={classes.clearFiltersButton}
              variant="outlined"
              startIcon={<Clear />}
            >
              Limpiar filtros
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Meetings List */}
      <Card className={classes.meetingsCard}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" style={{ color: '#667eea', fontWeight: 600 }}>
              <Event style={{ marginRight: 8, verticalAlign: 'middle', color: '#667eea' }} />
              Reuniones del Día ({filteredMeetings.length})
            </Typography>
          </Box>
          
          {filteredMeetings.length === 0 ? (
            <Box className={classes.emptyState}>
              <EventAvailable style={{ fontSize: 48, marginBottom: 16, color: 'inherit' }} />
              <Typography variant="h6" gutterBottom>
                No hay reuniones para este día
              </Typography>
              <Typography variant="body2">
                Selecciona otro día o ajusta los filtros de hora
              </Typography>
            </Box>
          ) : (
            <Box>
              {filteredMeetings.map((meeting) => {
                const status = getMeetingStatus(
                  meeting.scheduledStart,
                  meeting.scheduledEnd,
                  meeting.timeZone
                );
                const statusConfig = getStatusConfig(status);
                const startTime = formatTime(meeting.scheduledStart, meeting.timeZone);
                const endTime = formatTime(meeting.scheduledEnd, meeting.timeZone);

                return (
                  <Card
                    key={meeting.id}
                    className={`${classes.meetingItem} ${statusConfig.className}`}
                    onClick={() => onMeetingClick && onMeetingClick(meeting.id)}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="flex-start" gap={3}>
                        {/* Time Display */}
                        <Box className={classes.timeDisplay}>
                          <Typography className={classes.timeStart}>
                            {startTime}
                          </Typography>
                          <Typography className={classes.timeEnd}>
                            {endTime}
                          </Typography>
                        </Box>

                        <Divider orientation="vertical" flexItem />

                        {/* Meeting Info */}
                        <Box flex={1}>
                          <Typography className={classes.meetingTitle} variant="h6">
                            {meeting.title}
                          </Typography>
                          {meeting.subject && (
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                              {meeting.subject}
                            </Typography>
                          )}
                          
                          <Box className={classes.organizerInfo}>
                            <Person style={{ fontSize: 16, color: '#666' }} />
                            <Typography variant="body2" color="textSecondary">
                              {meeting.organizer.name}
                            </Typography>
                          </Box>

                          <Box display="flex" alignItems="center" gap={2} mt={2}>
                            <Chip
                              icon={React.createElement(statusConfig.icon)}
                              label={statusConfig.label}
                              color={statusConfig.color}
                              size="small"
                              className={classes.statusChip}
                            />
                            <Typography 
                              variant="caption" 
                              style={{
                                color: meeting.organizer.hasJoined
                                  ? '#4caf50'
                                  : status === 'in_progress'
                                  ? '#ff9800'
                                  : '#9e9e9e'
                              }}
                            >
                              {meeting.organizer.hasJoined
                                ? '✓ Organizador unido'
                                : status === 'in_progress'
                                ? 'Organizador no unido'
                                : '—'}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Avatar */}
                        <Box position="relative">
                          <Avatar 
                            className={classes.avatar}
                            src={meeting.organizer.photoUrl}
                            alt={meeting.organizer.name}
                          >
                            {!meeting.organizer.photoUrl && getInitials(meeting.organizer.name)}
                          </Avatar>
                          <Box
                            style={{
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              backgroundColor: meeting.organizer.hasJoined ? '#4caf50' : '#9e9e9e',
                              border: '2px solid white',
                              position: 'absolute',
                              bottom: -2,
                              right: -2,
                            }}
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export { CalendarView };
export default CalendarView;